<template>
  <div class="relative">
    <!-- Header buttons -->
    <div 
      class="flex flex-row gap-1 mb-2" 
      v-if="props.showAddFileButton || props.showUploadButton || props.showClearButton"
    >
      <button
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-primary hover:brightness-150 hover:shadow-sm hover:shadow-black"
        @click="triggerFileInput()"
        v-if="props.showAddFileButton"
      >
        <i class="text-white pi pi-plus" />
      </button>
      <button
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-primary hover:brightness-150 hover:shadow-sm hover:shadow-black"
        @click="uploadFile()"
        v-if="props.showUploadButton"
      >
        <i class="text-white pi pi-upload" />
      </button>
      <button
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-primary hover:brightness-150 hover:shadow-sm hover:shadow-black"
        @click="clearFile()"
        v-if="props.showClearButton"
      >
        <i class="text-white pi pi-times" />
      </button>
    </div>

    <!-- File upload area -->
    <div
      class="border-dashed border-2 rounded-xl border-[#8A9DA7] p-0 py-1 px-1 transition-all duration-200"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      :class="{ 'border-primary bg-primary/10': isDragging }"
    >
      <!-- Hidden file input -->
      <input
        type="file"
        ref="fileInput"
        :name="props.name + '[]'"
        :accept="props.accepts"
        @change="onFileSelect"
        class="hidden"
      />

      <!-- Empty state or loading -->
      <div v-if="uploadedFiles.length === 0" class="flex flex-row justify-center p-0">
        <button
          type="button"
          class="flex flex-row justify-center w-full py-4 transition-all duration-200"
          :class="isDragging ? 'opacity-100' : 'opacity-60'"
          @click="triggerFileInput()"
          v-if="!loading"
        >
          <div class="flex flex-col items-center gap-2">
            <i 
              class="text-6xl pi pi-cloud-upload transition-transform duration-200"
              :class="{ 'scale-110 text-primary': isDragging }"
            ></i>
            <div 
              class="transition-all duration-200"
              :class="{ 'text-primary font-semibold': isDragging }"
            >
              {{ isDragging ? 'Drop file to upload' : uploadPlaceHolder }}
            </div>
          </div>
        </button>
        <div class="flex flex-row justify-center py-4" v-else>
          <i class="text-4xl pi pi-spinner animate-spin"></i>
        </div>
      </div>

      <!-- File preview -->
      <div v-else>
        <UiFilePreview
          download-label="Download"
          :file="file"
          v-for="(file, index) in uploadedFiles"
          :key="index"
          @remove="removeFile(index)"
        ></UiFilePreview>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IBlurEvent, IChangeEvent, IInputEvent } from "~/scad-shared/types/interfaces";

const props = withDefaults(
  defineProps<{
    name: string;
    url: string;
    showAddFileButton?: boolean;
    showUploadButton?: boolean;
    showClearButton?: boolean;
    autoUpload?: boolean;
    fileLimit?: number;
    lookupUrl?: string;
    tableId?: number;
    accepts?: string;
  }>(),
  {
    fileLimit: 1,
    autoUpload: true,
    showAddFileButton: false,
    showUploadButton: false,
    showClearButton: false,
  }
);

const fileInput = ref<HTMLInputElement>();
const uploadedFiles = ref<File[]>([]);
const isDragging = ref(false);

const toast = useToast();

// const value: Ref<string> = ref(props.value ? props.value : "");
const value = defineModel<string>("value");
const form = defineModel<Indexable>("form");
const block = defineModel<boolean>("block", {
  default: false,
});

const loading: Ref<boolean> = ref(false);
let uploadPlaceHolder: string = "Drop files here to upload";
if (
  props.name &&
  props.name !== "" &&
  props.name.toLowerCase().includes("logo")
) {
  uploadPlaceHolder = uploadPlaceHolder.concat(" logo");
}

if (value.value && value.value !== "") {
  if (props.lookupUrl) {
    loading.value = true;
    onMounted(() => {
      let file: File;
      nextTick(async () => {
        try {
          const fileBlob = await $fetch<Blob>(props.lookupUrl as string, {
            responseType: "blob",
          });
          const fileUrlValues = value.value!.split("/");
          const filename = fileUrlValues[fileUrlValues.length - 1];
          file = new File([fileBlob], filename, {
            type: fileBlob.type,
          });

          // @ts-ignore
          file.objectURL = URL.createObjectURL(file);

          uploadedFiles.value.push(file);
        } catch (error) {
          uploadedFiles.value = [];
          fileNotFoundHander(value.value!);
        } finally {
          loading.value = false;
        }
      });
    });
  }
}

watch(
  () => value.value,
  async (newValue) => {
    if (
      props.lookupUrl &&
      newValue &&
      newValue !== "" &&
      newValue != value.value
    ) {
      loading.value = true;
      const fileBlob = await $fetch<Blob>(props.lookupUrl);
      const fileUrlValues = newValue.split("/");
      const filename = fileUrlValues[fileUrlValues.length - 1];
      const file: File = new File([fileBlob], filename, {
        type: fileBlob.type,
      });
      // @ts-ignore
      file.objectURL = URL.createObjectURL(file);
      nextTick(() => {
        uploadedFiles.value.push(file);
        loading.value = false;
      });
    } else if (newValue == null) {
      nextTick(() => {
        uploadedFiles.value = [];
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      });
    }
  }
);

const emit = defineEmits<{
  change: [event: IInputEvent];
  input: [event: IChangeEvent];
  blur: [event: IBlurEvent];
}>();

watch(
  () => value.value,
  (newValue) => {
    emit("change", { value: value.value });
    emit("input", { value: value.value });
  }
);

function triggerFileInput() {
  fileInput.value?.click();
}

function onDragOver(event: DragEvent) {
  isDragging.value = true;
}

function onDragLeave(event: DragEvent) {
  isDragging.value = false;
}

function onDrop(event: DragEvent) {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handleFile(files[0]);
  }
}

function onFileSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    handleFile(files[0]);
  }
}

function handleFile(file: File) {
  // Validate file size (30MB max)
  if (file.size > 30 * 1024 * 1024) {
    toast.add({
      summary: "File size exceeds 30MB. Please upload a smaller file.",
      severity: "error",
      life: 5000,
    });
    clearFileInput();
    return;
  }

  // Validate file type
  if (props.accepts && props.accepts.trim() !== "") {
    const acceptedTypes = props.accepts.split(",").map((type) => type.trim());
    const isValidType = acceptedTypes.includes(file.type);

    if (!isValidType) {
      toast.add({
        summary: "Unsupported file type. Please upload a valid file.",
        severity: "error",
        life: 5000,
      });
      clearFileInput();
      return;
    }
  }

  if (file.type === "") {
    toast.add({
      summary: "Unsupported file type. Please upload a valid file.",
      severity: "error",
      life: 5000,
    });
    clearFileInput();
    return;
  }

  // Create object URL for preview
  // @ts-ignore
  file.objectURL = URL.createObjectURL(file);
  
  // Replace existing file (single file only)
  uploadedFiles.value = [file];

  // Auto upload if enabled
  if (props.autoUpload) {
    uploadFile();
  }
}

async function uploadFile() {
  if (uploadedFiles.value.length === 0) return;

  loading.value = true;
  try {
    const formData = new FormData();
    formData.append("file", uploadedFiles.value[0]);

    const response: any = await $fetch(props.url.replaceAll("_", ""), {
      method: "PATCH",
      body: formData,
    });

    value.value = response.Data.FilePath;
    uploadCompleteHandler();
  } catch (error) {
    toast.add({
      summary: "File upload failed. Please try again.",
      severity: "error",
      life: 5000,
    });
  } finally {
    loading.value = false;
  }
}

function clearFileInput() {
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function uploadCompleteHandler() {
  toast.add({
    summary: "File successfully uploaded!",
    //   detail: e.response._data.ResponseStatus.Message,
    severity: "info",
    life: 5000,
  });
}

function removeFile(fileIndex: number) {
  uploadedFiles.value.splice(fileIndex, 1);
  value.value = "";
  clearFileInput();
}

function clearFile() {
  uploadedFiles.value = [];
  value.value = "";
  clearFileInput();
}

function fileNotFoundHander(filepath: string) {
  const pos = filepath.lastIndexOf("/");
  const errorFilePath: string =
    pos == -1 ? filepath : filepath.substring(pos + 1);
  toast.add({
    summary: `The file with the path ${errorFilePath} was not found.`,
    //   detail: e.response._data.ResponseStatus.Message,
    severity: "error",
    life: 5000,
  });
}

const fileUploadValidation = ref<string | null>(null);

const computedFileUploadValidation = computed(() => {
  return fileUploadValidation.value || undefined;
});

onMounted(() => {
  nextTick(async () => {
    // get validation form meta connection table
    interface ValidationResponse {
      Results: {
        MetaTableId: number;
        Validation?: string;
      }[];
    }

    const validationResponse = await $fetch<ValidationResponse>(
      `/scad/scadfw/records/meta/MetaConnection`
    );
    if (validationResponse.Results.length > 0) {
      const results = validationResponse.Results;
      results.forEach((rec) => {
        if (rec.MetaTableId === props.tableId) {
          fileUploadValidation.value = rec.Validation || null;
        }
      });
    }
  });
});
</script>

<style scoped>
.p-progressbar {
  display: none !important;
}
</style>
